"use client";

import {JSX} from "react";

import Image from "next/image";

import {type LocationOption, type LocationSearchInstance} from "@/features/locations";

import {useModelSnapshot} from "@/shared/hooks/state-management/use-model-snapshot";
import {useTranslation} from "@/shared/i18n";
import {Spinner} from "@/shared/ui/feedback/spinner";
import {TextField} from "@/shared/ui/form/fields";
import {getStaticPathIcon} from "@/shared/utils/get-static-path-icon";

import {LocationSection} from "./parts/location-section-list";

import "./styles/location-search-drawer.scss";

interface LocationSearchDrawerViewProps {
    model: LocationSearchInstance;
    placeholder?: string;
    className?: string;
    isFullscreen?: boolean;
    onFocusChange?: (isFocused: boolean) => void;
    onSelect?: () => void;
}

export function LocationSearchDrawerView({
    model,
    placeholder,
    className,
    isFullscreen,
    onFocusChange,
    onSelect,
}: LocationSearchDrawerViewProps): JSX.Element {
    const snapshot = useModelSnapshot(model);
    const {t} = useTranslation("search");

    const handleOptionClick = (option: LocationOption): void => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        model.selectOption(option);
        onFocusChange?.(false);
        onSelect?.();
    };

    const handleClear = (): void => {
        model.clear();
    };

    const handleFocus = (): void => {
        onFocusChange?.(true);
    };

    const handleBack = (): void => {
        onFocusChange?.(false);
    };

    const searchIcon = <Image src={getStaticPathIcon("search")} alt="" width={20} height={20} />;

    const backArrow = (
        <button type="button" onClick={handleBack} className="location-search-drawer__back" aria-label="Back">
            <Image src={getStaticPathIcon("arrow-left")} alt="" width={20} height={20} />
        </button>
    );

    return (
        <div className={`location-search-drawer ${className || ""}`}>
            <div className="location-search-drawer__input-wrapper" onFocus={handleFocus}>
                <TextField
                    model={model.input}
                    autoComplete="off"
                    type="text"
                    size="lg"
                    autoCorrect="off"
                    autoCapitalize="off"
                    placeholder={placeholder || t("locationPlaceholder")}
                    icon={isFullscreen ? backArrow : searchIcon}
                    iconPosition="left"
                    className={`location-search-drawer__field ${snapshot.hasError ? "location-search-drawer__field--error" : ""}`}
                    action={
                        snapshot.shouldShowLoading ? (
                            <Spinner size="small" />
                        ) : snapshot.input.value ? (
                            <button type="button" onClick={handleClear} className="location-search-drawer__clear" aria-label={t("$.common.clear")}>
                                ×
                            </button>
                        ) : null
                    }
                />
                {snapshot.hasError && <div className="location-search-drawer__error">{t(`$.${snapshot.error}` as Parameters<typeof t>[0])}</div>}
            </div>

            {(snapshot.hasResults || snapshot.hasEmptyResults) && (
                <div className="location-search-drawer__results" role="listbox" aria-label={t("locationPlaceholder")}>
                    {snapshot.hasResults && (
                        <>
                            <LocationSection
                                title={t("countries")}
                                options={snapshot.filteredOptions.countries}
                                selectedOptionId={snapshot.selectedOption?.id}
                                onOptionClick={handleOptionClick}
                                sectionId="countries"
                            />

                            <LocationSection
                                title={t("states")}
                                options={snapshot.filteredOptions.states}
                                selectedOptionId={snapshot.selectedOption?.id}
                                onOptionClick={handleOptionClick}
                                sectionId="states"
                            />
                            <LocationSection
                                title={t("destinations")}
                                options={snapshot.filteredOptions.destinations}
                                selectedOptionId={snapshot.selectedOption?.id}
                                onOptionClick={handleOptionClick}
                                sectionId="destinations"
                            />

                            <LocationSection
                                title={t("cities")}
                                options={snapshot.filteredOptions.cities}
                                selectedOptionId={snapshot.selectedOption?.id}
                                onOptionClick={handleOptionClick}
                                sectionId="cities"
                            />

                            <LocationSection
                                title={t("rentals")}
                                options={snapshot.filteredOptions.rentals}
                                selectedOptionId={snapshot.selectedOption?.id}
                                onOptionClick={handleOptionClick}
                                sectionId="rentals"
                            />
                        </>
                    )}

                    {snapshot.hasEmptyResults && <div className="location-search-drawer__empty">{t("noResultsFound")}</div>}
                </div>
            )}
        </div>
    );
}
